import React, { useCallback, useState } from 'react';
import { Select, Input, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjs';
import useDiagnosisOptions from 'hooks/useDiagnosisOptions';
import { TVisitDiagnosisEntry } from 'types/insuranceBilling';
import s from './s.module.less';

const MAX_DIAGNOSES = 12;
const MAX_CODE_LENGTH = 10;
const MAX_DESC_LENGTH = 50;

let nextId = -1;

const ADD_NEW_SENTINEL = '__ADD_NEW__';

type DiagnosisCodeSelectProps = {
    value?: string;
    options: { code: string; name: string }[];
    onSelect: (code: string) => void;
    onAddCustom: (searchValue: string) => void;
};

const DiagnosisCodeSelect = ({ value, options, onSelect, onAddCustom }: DiagnosisCodeSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const [customCodes, setCustomCodes] = useState<string[]>([]);

    const allOptions = React.useMemo(() => {
        const items: { label: React.ReactNode; value: string }[] = [];
        const addedValues = new Set<string>();
        if (value && !options.some((opt) => opt.code === value) && !customCodes.includes(value)) {
            items.push({ label: value, value });
            addedValues.add(value);
        }
        customCodes.forEach((code) => {
            if (!options.some((opt) => opt.code === code) && !addedValues.has(code)) {
                items.push({ label: code, value: code });
                addedValues.add(code);
            }
        });
        options.forEach((opt) => {
            items.push({ label: `${opt.code} — ${opt.name}`, value: opt.code });
        });
        return items;
    }, [value, customCodes, options]);

    const displayOptions = React.useMemo(() => {
        if (!searchValue) return allOptions;
        const search = searchValue.toLowerCase();
        const filtered = allOptions.filter((opt) => String(opt.label).toLowerCase().includes(search));
        if (filtered.length === 0) {
            if (searchValue.trim().length > MAX_CODE_LENGTH) {
                return [
                    {
                        label: (
                            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                                Code must be {MAX_CODE_LENGTH} characters or less
                            </span>
                        ),
                        value: ADD_NEW_SENTINEL,
                        disabled: true,
                    },
                ];
            }
            return [
                {
                    label: (
                        <span style={{ fontSize: '12px' }}>
                            Code not found. <span className={s.addNewLink}>Add new code</span>
                        </span>
                    ),
                    value: ADD_NEW_SENTINEL,
                },
            ];
        }
        return filtered;
    }, [allOptions, searchValue]);

    const handleSelect = (val: string) => {
        if (val === ADD_NEW_SENTINEL) {
            const trimmed = searchValue.trim();
            if (trimmed) {
                setCustomCodes((prev) => [...prev, trimmed]);
                onAddCustom(trimmed);
            }
        } else {
            onSelect(val);
        }
        setSearchValue('');
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder="Search or select a diagnosis"
            searchValue={searchValue}
            onSearch={setSearchValue}
            onSelect={handleSelect}
            filterOption={false}
            optionLabelProp="value"
            className={s.codeSelect}
            notFoundContent={null}
            options={displayOptions}
        />
    );
};

type Props = {
    value: TVisitDiagnosisEntry[];
    onChange: (entries: TVisitDiagnosisEntry[]) => void;
    specialty?: string;
    bordered?: boolean;
    footer?: React.ReactNode;
};

const DiagnosisSection = ({ value, onChange, specialty, bordered, footer }: Props) => {
    const [diagnosisOptions] = useDiagnosisOptions(specialty);

    const handleSelectCode = useCallback(
        (entryId: number, code: string) => {
            const matched = diagnosisOptions.find((opt) => opt.code === code);
            if (matched) {
                const next = value.map((entry) =>
                    entry.id === entryId ? { ...entry, code: matched.code, description: matched.name, isCustom: false } : entry,
                );
                onChange(next);
            }
        },
        [diagnosisOptions, value, onChange],
    );

    const handleAddCustomCode = useCallback(
        (entryId: number, searchValue: string) => {
            const trimmed = searchValue.trim();
            if (trimmed && trimmed.length <= MAX_CODE_LENGTH) {
                const next = value.map((entry) =>
                    entry.id === entryId
                        ? { ...entry, code: trimmed, description: '', isCustom: true }
                        : entry,
                );
                onChange(next);
            }
        },
        [value, onChange],
    );

    const handleDescriptionChange = useCallback(
        (entryId: number, description: string) => {
            const next = value.map((entry) => (entry.id === entryId ? { ...entry, description } : entry));
            onChange(next);
        },
        [value, onChange],
    );

    const handleAdd = useCallback(() => {
        if (value.length >= MAX_DIAGNOSES) return;
        onChange([...value, { id: nextId--, description: '', isPrimary: value.length === 0 }]);
    }, [value, onChange]);

    const handleRemove = useCallback(
        (id: number) => {
            let next = value.filter((entry) => entry.id !== id);
            if (next.length > 0 && !next.some((e) => e.isPrimary)) {
                next = next.map((e, i) => (i === 0 ? { ...e, isPrimary: true } : e));
            }
            onChange(next);
        },
        [value, onChange],
    );

    return (
        <div className={`${s.wrap} ${bordered ? s.bordered : ''}`}>
            <div className={s.header}>
                <h3 className={s.title}>Diagnosis</h3>
                <p className={s.subtitle}>
                    Add the diagnoses for this visit. If you use Klarity to submit claims, at least one diagnosis is
                    required. Claims cannot be submitted without it.
                </p>
            </div>
            {value.map((entry) => {
                const selectedCodes = value.filter((e) => e.id !== entry.id && e.code).map((e) => e.code);
                const filteredOptions = diagnosisOptions.filter((opt) => !selectedCodes.includes(opt.code));

                return (
                    <div key={entry.id} className={s.entryRow}>
                        <div className={s.codeField}>
                            <div className={s.fieldLabel}>
                                Diagnosis code
                                <Tag className={entry.isPrimary ? s.primaryTag : s.addOnTag}>
                                    {entry.isPrimary ? 'Primary' : 'Add-on'}
                                </Tag>
                            </div>
                            <DiagnosisCodeSelect
                                value={entry.code}
                                options={filteredOptions}
                                onSelect={(val) => handleSelectCode(entry.id, val)}
                                onAddCustom={(val) => handleAddCustomCode(entry.id, val)}
                            />
                            {entry.lastUsedAt && (
                                <div className={s.lastUsed}>
                                    Last used{' '}
                                    <span className={s.lastUsedDate}>
                                        {dayjs(entry.lastUsedAt).format('MMM DD, YYYY')}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className={s.descRow}>
                            <div className={s.descField}>
                                <div className={s.fieldLabel}>Description</div>
                                <Input
                                    value={entry.description}
                                    placeholder="None selected"
                                    readOnly={!entry.isCustom}
                                    maxLength={MAX_DESC_LENGTH}
                                    onChange={(e) =>
                                        entry.isCustom && handleDescriptionChange(entry.id, e.target.value)
                                    }
                                />
                            </div>
                            <span className={s.removeBtn} onClick={() => handleRemove(entry.id)}>
                                <DeleteOutlined />
                            </span>
                        </div>
                    </div>
                );
            })}
            <span className={`${s.addBtn} ${value.length >= MAX_DIAGNOSES ? s.disabled : ''}`} onClick={handleAdd}>
                <PlusOutlined /> Add diagnosis
            </span>
            {footer}
        </div>
    );
};

export default DiagnosisSection;
